import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const CLOUDMAILIN_BASE = "a29e53d141a11bf8be48@cloudmailin.net";

function buildAddress(token: string): string {
  // Plus-suffix routing on free CloudMailin: base+u_token@cloudmailin.net
  const [local, domain] = CLOUDMAILIN_BASE.split("@");
  return `${local}+u_${token}@${domain}`;
}

function generateToken(): string {
  // 6 chars, lowercase alphanumeric, no ambiguous chars
  const alphabet = "abcdefghjkmnpqrstuvwxyz23456789";
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < bytes.length; i++) out += alphabet[bytes[i] % alphabet.length];
  return out;
}

export const getOrCreateForwardAddress = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const { data: existing } = await supabase
      .from("forward_addresses")
      .select("address_token, last_received_at")
      .eq("user_id", userId)
      .maybeSingle();

    if (existing) {
      return {
        token: existing.address_token,
        address: buildAddress(existing.address_token),
        lastReceivedAt: existing.last_received_at,
      };
    }

    // Try a few times in the rare case of token collision
    for (let attempt = 0; attempt < 5; attempt++) {
      const token = generateToken();
      const { data, error } = await supabase
        .from("forward_addresses")
        .insert({ user_id: userId, address_token: token })
        .select("address_token, last_received_at")
        .single();
      if (!error && data) {
        return {
          token: data.address_token,
          address: buildAddress(data.address_token),
          lastReceivedAt: data.last_received_at,
        };
      }
    }
    throw new Response("Could not allocate forward address", { status: 500 });
  });

export const listForwardedEmails = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const { data, error } = await supabase
      .from("forwarded_emails")
      .select("id, from_address, subject, received_at, classified, rejection_signal, application_id")
      .eq("user_id", userId)
      .order("received_at", { ascending: false })
      .limit(10);

    if (error) throw new Response(error.message, { status: 500 });
    return { emails: data ?? [] };
  });
